import { useEffect, useMemo, useState } from 'react';
import type { BookingSlot, SlotsByDate } from '@/features/campaigns/types/campaign';
import { useAvailableSlotsMutation } from '@/features/campaigns/api/useCampaignMutations';
import { useManualAlertOptional } from '@/features/campaigns/components/ManualBuilder/ManualAlert';
import { getErrorMessage } from '@/shared/api/errors';
import { enumerateDates } from '@/features/campaigns/utils/campaignUtils';
import { MAX_AVAILABILITY_QUERY_DAYS } from './scheduleStep.constants';
import {
  formatChipDate,
  getFullyBookedDates,
  getDatesMissingSlots,
  findDayDataInSlots,
  isSlotAvailable,
  normalizeDateKey,
} from './scheduleStepHelpers';
import type { ScheduleStepProps } from './ScheduleStep.types';

export function useScheduleStep({
  billboardIds,
  campaignData,
  submitData,
  setSubmitData,
}: ScheduleStepProps) {
  const allDates = useMemo(
    () => enumerateDates(campaignData.startDate, campaignData.endDate, 366),
    [campaignData.startDate, campaignData.endDate],
  );
  // Show the full campaign range (same as screen-wise). Availability is still
  // fetched in ≤15-day batches via fetchAvailableSlots.
  const dates = allDates;
  const [activeDate, setActiveDate] = useState(dates[0] || '');
  const [slotsData, setSlotsData] = useState<SlotsByDate>({});
  const [slotsLoading, setSlotsLoading] = useState(true);
  const [applyingToAll, setApplyingToAll] = useState(false);
  const [appliedToAll, setAppliedToAll] = useState(() => {
    if (submitData.appliedToAll !== undefined) {
      return Boolean(submitData.appliedToAll);
    }
    if (allDates.length > 1 && submitData.bookingSlots.length > 0) {
      const datesWithSlots = new Set(
        submitData.bookingSlots.map((s) => normalizeDateKey(s.date)),
      );
      return allDates.every((d) => datesWithSlots.has(normalizeDateKey(d)));
    }
    return false;
  });

  useEffect(() => {
    if (submitData.appliedToAll !== undefined) {
      setAppliedToAll(Boolean(submitData.appliedToAll));
    } else if (allDates.length > 1 && submitData.bookingSlots.length > 0) {
      const datesWithSlots = new Set(
        submitData.bookingSlots.map((s) => normalizeDateKey(s.date)),
      );
      if (allDates.every((d) => datesWithSlots.has(normalizeDateKey(d)))) {
        setAppliedToAll(true);
      }
    }
  }, [submitData.appliedToAll, submitData.bookingSlots, allDates]);
  const alert = useManualAlertOptional();
  const slotsMutation = useAvailableSlotsMutation({ silent: true });

  useEffect(() => {
    if (dates[0]) setActiveDate(dates[0]);
  }, [dates]);

  useEffect(() => {
    if (!allDates.length || !billboardIds.length) {
      setSlotsData({});
      setSlotsLoading(false);
      return;
    }

    setSlotsLoading(true);
    setSlotsData({});
    slotsMutation.mutate(
      {
        dates: allDates,
        billboardIds,
        customDuration: campaignData.customDuration || 30,
      },
      {
        onSuccess: (res) => {
          const data = res?.data || {};
          setSlotsData(data);
          setSlotsLoading(false);
          const bookedDates = getFullyBookedDates(allDates, data);
          setSubmitData((prev) => ({
            ...prev,
            fullyBookedDates: bookedDates,
            slotsData: data,
          }));
        },
        onError: (error) => {
          setSlotsData({});
          setSlotsLoading(false);
          alert?.error(
            'Couldn’t load slots',
            getErrorMessage(error, 'Failed to load availability. Try again.'),
            { actions: [{ label: 'OK', primary: true }] },
          );
        },
        onSettled: () => {
          setSlotsLoading(false);
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch when dates/screens change
  }, [allDates.join(','), billboardIds.join(','), campaignData.customDuration]);

  const fullyBookedDates = useMemo(
    () => (slotsLoading ? [] : getFullyBookedDates(allDates, slotsData)),
    [allDates, slotsData, slotsLoading],
  );

  const activeAvailableDates = useMemo(
    () => allDates.filter((d) => !fullyBookedDates.includes(d)),
    [allDates, fullyBookedDates],
  );

  const missingDates = useMemo(
    () => getDatesMissingSlots(submitData.bookingSlots, allDates, undefined, fullyBookedDates),
    [submitData.bookingSlots, allDates, fullyBookedDates],
  );

  useEffect(() => {
    if (!slotsLoading && Object.keys(slotsData).length > 0) {
      const computed = getFullyBookedDates(allDates, slotsData);
      setSubmitData((prev) => {
        const prevFullyBooked = prev.fullyBookedDates || [];
        const isSame =
          prevFullyBooked.length === computed.length &&
          prevFullyBooked.every((d, i) => d === computed[i]) &&
          prev.slotsData === slotsData;
        if (isSame) return prev;
        return { ...prev, fullyBookedDates: computed, slotsData };
      });
    }
  }, [allDates, slotsData, slotsLoading, setSubmitData]);

  const syncRepsAllDates =
    submitData.syncRepsAllDates !== undefined
      ? Boolean(submitData.syncRepsAllDates)
      : true;
  /**
   * Upsert `hour` on every campaign date (respecting per-date availability),
   * using `repetitions` capped to each date's remaining capacity.
   */
  const copyHourToAllDates = (
    slots: BookingSlot[],
    hour: number,
    repetitions: number,
  ): BookingSlot[] => {
    const reps = Math.max(1, repetitions);
    const others = slots.filter((s) => s.hour !== hour);
    const copies: BookingSlot[] = [];
    for (const d of allDates) {
      const dayData = findDayDataInSlots(d, slotsData);
      if (dayData && Array.isArray(dayData.availableSlots)) {
        const avail = dayData.availableSlots.find((h) => h.hour === hour);
        if (!avail || !isSlotAvailable(avail)) continue;
        const max = Math.max(1, avail.availableRepetitions ?? reps);
        copies.push({ date: d, hour, repetitions: Math.min(reps, max) });
      } else {
        copies.push({ date: d, hour, repetitions: reps });
      }
    }
    return [...others, ...copies];
  };

  const setSyncRepsAllDates = (val: boolean) => {
    setSubmitData((prev) => {
      if (!val) {
        // Turning OFF: keep only the active date's slots; clear the copies.
        const normActive = normalizeDateKey(activeDate);
        return {
          ...prev,
          syncRepsAllDates: false,
          appliedToAll: false,
          bookingSlots: prev.bookingSlots.filter(
            (s) => normalizeDateKey(s.date) === normActive,
          ),
        };
      }
      // Turning ON: copy hours already booked on the active date to all dates.
      const normActive = normalizeDateKey(activeDate);
      const activeSlots = prev.bookingSlots.filter(
        (s) => normalizeDateKey(s.date) === normActive,
      );
      let bookingSlots = prev.bookingSlots;
      for (const slot of activeSlots) {
        bookingSlots = copyHourToAllDates(
          bookingSlots,
          slot.hour,
          slot.repetitions,
        );
      }
      return {
        ...prev,
        syncRepsAllDates: true,
        bookingSlots,
        appliedToAll: activeSlots.length > 0 ? true : prev.appliedToAll,
      };
    });
  };

  const activeDayData = findDayDataInSlots(activeDate, slotsData);
  const hours = activeDayData?.availableSlots ?? [];
  const canFetchSlots = dates.length > 0 && billboardIds.length > 0;
  const isLoadingSlots = canFetchSlots && slotsLoading;
  const selectedCount = submitData.bookingSlots.length;
  const activeDayCount = submitData.bookingSlots.filter(
    (s) => normalizeDateKey(s.date) === normalizeDateKey(activeDate),
  ).length;
  const showRangeNote = allDates.length > MAX_AVAILABILITY_QUERY_DAYS;

  const slotsByDate = useMemo(() => {
    const map = new Map<string, BookingSlot[]>();
    for (const slot of submitData.bookingSlots) {
      const list = map.get(slot.date) || [];
      list.push(slot);
      map.set(slot.date, list);
    }
    for (const list of map.values()) {
      list.sort((a, b) => a.hour - b.hour);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [submitData.bookingSlots]);

  const isSelected = (date: string, hour: number) => {
    const norm = normalizeDateKey(date);
    return submitData.bookingSlots.some(
      (s) => normalizeDateKey(s.date) === norm && s.hour === hour,
    );
  };

  const getSlot = (date: string, hour: number) => {
    const norm = normalizeDateKey(date);
    return submitData.bookingSlots.find(
      (s) => normalizeDateKey(s.date) === norm && s.hour === hour,
    );
  };

  const toggleSlot = (
    date: string,
    hour: number,
    maxReps: number,
    syncAll = syncRepsAllDates,
  ) => {
    if (maxReps <= 0) return;
    setSubmitData((prev) => {
      const normActive = normalizeDateKey(date);
      const exists = prev.bookingSlots.some(
        (s) => normalizeDateKey(s.date) === normActive && s.hour === hour,
      );
      let bookingSlots: BookingSlot[];
      if (exists) {
        if (syncAll) {
          bookingSlots = prev.bookingSlots.filter((s) => s.hour !== hour);
        } else {
          bookingSlots = prev.bookingSlots.filter(
            (s) => !(normalizeDateKey(s.date) === normActive && s.hour === hour),
          );
        }
      } else {
        if (syncAll) {
          const repCount = Math.min(1, maxReps) || 1;
          const slotsToAdd: BookingSlot[] = [];
          for (const d of allDates) {
            const dayData = findDayDataInSlots(d, slotsData);
            if (dayData && Array.isArray(dayData.availableSlots)) {
              const avail = dayData.availableSlots.find((h) => h.hour === hour);
              if (avail && isSlotAvailable(avail)) {
                const max = avail.availableRepetitions ?? repCount;
                slotsToAdd.push({
                  date: d,
                  hour,
                  repetitions: Math.min(repCount, Math.max(1, max)),
                });
              }
            } else {
              // No availability payload for this day yet (or empty) — still
              // include it so appliedToAll covers the full campaign range
              // (same behavior as applyToAllVisible / screen-wise sync).
              slotsToAdd.push({
                date: d,
                hour,
                repetitions: repCount,
              });
            }
          }
          const otherSlots = prev.bookingSlots.filter((s) => s.hour !== hour);
          bookingSlots = [...otherSlots, ...slotsToAdd];
        } else {
          bookingSlots = [
            ...prev.bookingSlots,
            { date, hour, repetitions: Math.min(1, maxReps) || 1 },
          ];
        }
      }
      return {
        ...prev,
        bookingSlots,
        appliedToAll: syncAll ? true : prev.appliedToAll,
      };
    });
  };

  const setReps = (
    date: string,
    hour: number,
    repetitions: number,
    syncAll = syncRepsAllDates,
  ) => {
    const validReps = Math.max(1, repetitions);
    setSubmitData((prev) => {
      if (syncAll) {
        return {
          ...prev,
          bookingSlots: copyHourToAllDates(prev.bookingSlots, hour, validReps),
          appliedToAll: true,
        };
      }
      return {
        ...prev,
        bookingSlots: prev.bookingSlots.map((s) => {
          if (
            normalizeDateKey(s.date) === normalizeDateKey(date) &&
            s.hour === hour
          ) {
            const dayData = findDayDataInSlots(s.date, slotsData);
            const avail = dayData?.availableSlots?.find((h) => h.hour === hour);
            const maxForDate = avail?.availableRepetitions ?? validReps;
            return {
              ...s,
              repetitions: Math.min(validReps, Math.max(1, maxForDate)),
            };
          }
          return s;
        }),
      };
    });
  };

  const removeSlot = (date: string, hour: number) => {
    const normTarget = normalizeDateKey(date);
    setSubmitData((prev) => ({
      ...prev,
      bookingSlots: syncRepsAllDates
        ? prev.bookingSlots.filter((s) => s.hour !== hour)
        : prev.bookingSlots.filter(
            (s) => !(normalizeDateKey(s.date) === normTarget && s.hour === hour),
          ),
      appliedToAll: prev.appliedToAll,
    }));
  };

  const clearAll = () => {
    setAppliedToAll(false);
    setSubmitData((prev) => ({ ...prev, bookingSlots: [], appliedToAll: false }));
  };

  const removeFromAllDates = () => {
    setSubmitData((prev) => ({
      ...prev,
      bookingSlots: prev.bookingSlots.filter((s) => s.date === activeDate),
      appliedToAll: false,
    }));
    setAppliedToAll(false);
  };

  const applyToAllVisible = async () => {
    if (appliedToAll) {
      removeFromAllDates();
      return;
    }

    const daySlots = submitData.bookingSlots.filter(
      (s) => s.date === activeDate,
    );
    if (!daySlots.length || applyingToAll) return;

    setApplyingToAll(true);
    try {
      let latest: SlotsByDate = slotsData;
      // Refresh full-range availability (batched ≤15 days per request in API).
      if (allDates.length > 0) {
        const res = await slotsMutation.mutateAsync({
          dates: allDates,
          billboardIds,
          customDuration: campaignData.customDuration || 30,
        });
        latest = { ...slotsData, ...(res?.data || {}) };
        setSlotsData(latest);
      }

      const next: BookingSlot[] = [];
      for (const date of allDates) {
        for (const slot of daySlots) {
          const dayHours = findDayDataInSlots(date, latest)?.availableSlots;
          const avail = dayHours?.find(
            (h) => h.hour === slot.hour && (h.availableRepetitions ?? 0) > 0,
          );
          if (avail) {
            const max = avail.availableRepetitions ?? 1;
            next.push({
              date,
              hour: slot.hour,
              repetitions: Math.min(slot.repetitions, max),
            });
          } else if (!dayHours) {
            next.push({ date, hour: slot.hour, repetitions: slot.repetitions });
          }
        }
      }
      setSubmitData((prev) => ({
        ...prev,
        bookingSlots: next,
        appliedToAll: true,
        syncRepsAllDates: true,
      }));
      setAppliedToAll(true);
    } catch {
      // toast handled by mutation
    } finally {
      setApplyingToAll(false);
    }
  };

  const activeLabel = formatChipDate(activeDate);
  const duration = Number(campaignData.customDuration) || 30;
  const defaultTotalReps = Math.floor(3600 / duration) || 1;

  return {
    allDates,
    dates,
    activeDate,
    setActiveDate,
    hours,
    canFetchSlots,
    isLoadingSlots,
    selectedCount,
    activeDayCount,
    showRangeNote,
    slotsByDate,
    isSelected,
    getSlot,
    toggleSlot,
    setReps,
    removeSlot,
    clearAll,
    applyingToAll,
    appliedToAll,
    applyToAllVisible,
    activeLabel,
    defaultTotalReps,
    syncRepsAllDates,
    setSyncRepsAllDates,
    fullyBookedDates,
    activeAvailableDates,
    missingDates,
  };
}
